import { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  IndustrySeriesIndicator,
  CreditSeriesItem,
  CreditCorrelation,
  CreditRiskTransmission,
} from "../../api";

// ── Color palette (matching credit-intel design system) ──────────────
const CHART_COLORS = [
  "#1A3A5C", // primary navy
  "#C9A96E", // accent gold
  "#3B82F6", // blue
  "#1B7D46", // green
  "#C4362C", // red
  "#6B7280", // gray
  "#D4A017", // amber
  "#0891B2", // cyan
];

// Credit overlay colors — intentionally distinct (warm/muted dashes)
const CREDIT_COLORS = [
  "#E07A5F", // terra cotta
  "#81B29A", // sage
  "#F2CC8F", // sand
];

const TIME_RANGES = [
  { key: "6m", label: "6M", months: 6 },
  { key: "1y", label: "1Y", months: 12 },
  { key: "3y", label: "3Y", months: 36 },
  { key: "all", label: "ALL", months: 999 },
] as const;

type TimeRange = (typeof TIME_RANGES)[number]["key"];

interface IndustryChartProps {
  indicators: Record<string, IndustrySeriesIndicator>;
  industry: string;
  // Credit overlay (optional)
  creditSeries?: CreditSeriesItem[];
  correlations?: CreditCorrelation[];
  riskTransmission?: CreditRiskTransmission | null;
  creditLoading?: boolean;
}

function shortPeriod(period: string): string {
  const m = period.match(/^(\d{4})-(\d{2})$/);
  if (m) return `${m[1].slice(2)}/${m[2]}`;
  return period;
}

function filterByTimeRange(periods: string[], range: TimeRange): string[] {
  const target = TIME_RANGES.find((r) => r.key === range);
  if (!target || target.months >= 999) return periods;

  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth() - target.months, 1);
  const cutoffStr = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, "0")}`;

  return periods.filter((p) => p >= cutoffStr);
}

/** Format correlation coefficient with color coding */
function corrBadge(r: number, label: string) {
  const abs = Math.abs(r);
  let strength: string;
  let bgClass: string;

  if (abs >= 0.7) {
    strength = "强";
    bgClass = r > 0
      ? "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800"
      : "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800";
  } else if (abs >= 0.4) {
    strength = "中";
    bgClass = "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800";
  } else {
    strength = "弱";
    bgClass = "bg-zinc-50 text-zinc-500 border-zinc-200 dark:bg-zinc-900 dark:text-zinc-400 dark:border-zinc-700";
  }

  return (
    <span
      key={label}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${bgClass}`}
      title={`Pearson r = ${r.toFixed(3)}`}
    >
      <span className="font-mono tabular-nums">{r > 0 ? "+" : ""}{r.toFixed(2)}</span>
      <span className="opacity-70">{strength}</span>
    </span>
  );
}

export default function IndustryChart({
  indicators,
  industry,
  creditSeries,
  correlations,
  riskTransmission,
  creditLoading,
}: IndustryChartProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>("3y");
  const [hiddenCodes, setHiddenCodes] = useState<Set<string>>(new Set());
  const [showCredit, setShowCredit] = useState(true);

  const indicatorList = useMemo(
    () => Object.values(indicators),
    [indicators]
  );

  const creditList = useMemo(
    () => (showCredit ? (creditSeries || []) : []),
    [creditSeries, showCredit]
  );

  // Collect all periods across industry + credit indicators
  const allPeriods = useMemo(() => {
    const periodSet = new Set<string>();
    for (const ind of indicatorList) {
      for (const p of Object.keys(ind.values)) {
        periodSet.add(p);
      }
    }
    for (const cs of creditList) {
      for (const p of Object.keys(cs.values)) {
        periodSet.add(p);
      }
    }
    return [...periodSet].sort();
  }, [indicatorList, creditList]);

  const filteredPeriods = useMemo(
    () => filterByTimeRange(allPeriods, timeRange),
    [allPeriods, timeRange]
  );

  // Build chart data
  const chartData = useMemo(() => {
    return filteredPeriods.map((period) => {
      const row: Record<string, unknown> = {
        period,
        shortPeriod: shortPeriod(period),
      };
      for (const ind of indicatorList) {
        const val = ind.values[period];
        if (val) {
          row[ind.code] = val.value;
        }
      }
      for (const cs of creditList) {
        const val = cs.values[period];
        if (val) {
          row[`credit_${cs.key}`] = val.value;
        }
      }
      return row;
    });
  }, [filteredPeriods, indicatorList, creditList]);

  // Axis logic
  const visibleIndustry = indicatorList.filter((ind) => !hiddenCodes.has(ind.code));
  const hasPercent = visibleIndustry.some((ind) => ind.unit === "%") || creditList.length > 0;
  const hasAbsolute = visibleIndustry.some((ind) => ind.unit !== "%");
  const dualAxis = hasPercent && hasAbsolute;

  const toggleCode = (code: string) => {
    setHiddenCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        if (next.size >= indicatorList.length - 1) return prev;
        next.add(code);
      }
      return next;
    });
  };

  // Best correlation per credit metric
  const topCorrelations = useMemo(() => {
    if (!correlations || correlations.length === 0) return [];
    const byCredit = new Map<string, CreditCorrelation>();
    for (const c of correlations) {
      const existing = byCredit.get(c.credit_indicator);
      if (!existing || Math.abs(c.pearson_r) > Math.abs(existing.pearson_r)) {
        byCredit.set(c.credit_indicator, c);
      }
    }
    return [...byCredit.values()];
  }, [correlations]);

  if (indicatorList.length === 0) {
    return (
      <div className="paper-panel p-8 text-center text-ink-tertiary text-sm">
        暂无 {industry} 的指标数据
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls bar */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        {/* Indicator toggles */}
        <div className="flex flex-wrap gap-1.5">
          {indicatorList.map((ind, i) => {
            const color = CHART_COLORS[i % CHART_COLORS.length];
            const hidden = hiddenCodes.has(ind.code);
            return (
              <button
                key={ind.code}
                onClick={() => toggleCode(ind.code)}
                className={`
                  flex items-center gap-1.5 px-2.5 py-1 rounded-full text-2xs font-medium
                  border transition-all duration-150 cursor-pointer
                  ${hidden
                    ? "border-border text-ink-tertiary opacity-50 hover:opacity-75"
                    : "border-border text-ink hover:shadow-sm"
                  }
                `}
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: hidden ? "#ccc" : color }}
                />
                {ind.name}
                <span className="text-ink-tertiary ml-0.5">({ind.unit})</span>
              </button>
            );
          })}

          {/* Credit toggle */}
          {creditSeries && creditSeries.length > 0 && (
            <button
              onClick={() => setShowCredit((v) => !v)}
              className={`
                flex items-center gap-1.5 px-2.5 py-1 rounded-full text-2xs font-medium
                border transition-all duration-150 cursor-pointer
                ${showCredit
                  ? "border-orange-300 dark:border-orange-700 text-orange-700 dark:text-orange-300 bg-orange-50/50 dark:bg-orange-950/20"
                  : "border-border text-ink-tertiary opacity-50 hover:opacity-75"
                }
              `}
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: showCredit ? "#E07A5F" : "#ccc" }} />
              信贷叠加
            </button>
          )}
          {creditLoading && (
            <span className="flex items-center gap-1.5 px-2.5 py-1 text-2xs text-ink-tertiary">
              <span className="w-3 h-3 border border-orange-400 border-t-transparent rounded-full animate-spin" />
              加载信贷数据...
            </span>
          )}
        </div>

        {/* Time range selector */}
        <div className="flex items-center gap-0.5 bg-surface-sunken rounded-lg p-0.5">
          {TIME_RANGES.map((tr) => (
            <button
              key={tr.key}
              onClick={() => setTimeRange(tr.key)}
              className={`
                px-2.5 py-1 rounded-md text-2xs font-medium transition-all duration-150 cursor-pointer
                ${timeRange === tr.key
                  ? "bg-surface-raised text-ink shadow-sm"
                  : "text-ink-tertiary hover:text-ink"
                }
              `}
            >
              {tr.label}
            </button>
          ))}
        </div>
      </div>

      {/* Correlation badges (if available) */}
      {topCorrelations.length > 0 && showCredit && (
        <div className="flex items-center gap-2 flex-wrap px-1">
          <span className="text-2xs text-ink-tertiary font-medium">相关性：</span>
          {topCorrelations.map((c) => (
            <div key={c.credit_key} className="flex items-center gap-1">
              <span className="text-2xs text-ink-secondary">{c.credit_indicator}</span>
              {corrBadge(c.pearson_r, c.credit_indicator)}
              <span className="text-[9px] text-ink-tertiary font-mono">
                vs {c.industry_indicator}
                <span className="ml-1 opacity-60">n={c.n_periods}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Chart */}
      <div className="paper-panel p-4 pt-5">
        <ResponsiveContainer width="100%" height={380}>
          <LineChart data={chartData} margin={{ top: 5, right: dualAxis ? 15 : 5, left: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #e5e5e5)" opacity={0.6} />
            <XAxis
              dataKey="shortPeriod"
              tick={{ fontSize: 10, fill: "var(--color-ink-tertiary, #9B9B9B)" }}
              tickLine={false}
              axisLine={{ stroke: "var(--color-border, #e5e5e5)" }}
              interval="preserveStartEnd"
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 10, fill: "var(--color-ink-tertiary, #9B9B9B)" }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            {dualAxis && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 10, fill: "var(--color-ink-tertiary, #9B9B9B)" }}
                tickLine={false}
                axisLine={false}
                width={50}
              />
            )}
            <Tooltip
              contentStyle={{
                fontSize: 11,
                backgroundColor: "var(--color-surface-raised, #fff)",
                border: "1px solid var(--color-border, #e5e5e5)",
                borderRadius: 8,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              }}
              formatter={(value: number, name: string) => {
                // Check credit series first
                if (name.startsWith("credit_")) {
                  const cs = creditList.find((c) => `credit_${c.key}` === name);
                  return [`${value}${cs?.unit || ""}`, cs?.label || name];
                }
                const ind = indicatorList.find((i) => i.code === name);
                return [
                  `${value}${ind?.unit || ""}`,
                  ind?.name || name,
                ];
              }}
              labelFormatter={(label) => `${label}`}
            />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              formatter={(value: string) => {
                if (value.startsWith("credit_")) {
                  const cs = creditList.find((c) => `credit_${c.key}` === value);
                  return cs?.label || value;
                }
                const ind = indicatorList.find((i) => i.code === value);
                return ind?.name || value;
              }}
            />

            {/* Industry indicator lines (solid) */}
            {indicatorList.map((ind, i) => {
              if (hiddenCodes.has(ind.code)) return null;
              const color = CHART_COLORS[i % CHART_COLORS.length];
              const yId = dualAxis && ind.unit !== "%" ? "right" : "left";
              return (
                <Line
                  key={ind.code}
                  yAxisId={yId}
                  type="monotone"
                  dataKey={ind.code}
                  stroke={color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 2 }}
                  connectNulls
                />
              );
            })}

            {/* Credit overlay lines (dashed) */}
            {creditList.map((cs, i) => (
              <Line
                key={`credit_${cs.key}`}
                yAxisId="left"
                type="monotone"
                dataKey={`credit_${cs.key}`}
                stroke={CREDIT_COLORS[i % CREDIT_COLORS.length]}
                strokeWidth={1.5}
                strokeDasharray="6 3"
                dot={false}
                activeDot={{ r: 3, strokeWidth: 1.5, fill: CREDIT_COLORS[i % CREDIT_COLORS.length] }}
                connectNulls
              />
            ))}

            {/* PMI 50 line (荣枯线) for percentage-based charts */}
            {visibleIndustry.some((ind) => ind.name.includes("PMI")) && (
              <ReferenceLine
                yAxisId="left"
                y={50}
                stroke="var(--color-ink-tertiary, #9B9B9B)"
                strokeDasharray="4 4"
                strokeWidth={1}
                opacity={0.5}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Risk transmission panel */}
      {riskTransmission && showCredit && (
        <div className="paper-panel overflow-hidden">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            <span className="text-xs font-semibold text-ink">⚡ 信贷风险传导</span>
            <span className="text-2xs text-ink-tertiary">— {riskTransmission.headline}</span>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="grid grid-cols-1 gap-3">
              {riskTransmission.channels.map((ch) => (
                <div
                  key={ch.name}
                  className="flex gap-3 p-3.5 rounded-lg bg-surface hover:bg-surface-sunken transition-colors"
                >
                  {/* Severity-colored left indicator */}
                  <div className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center mt-0.5 ${
                    ch.severity === "high"
                      ? "bg-red-100 dark:bg-red-900/30"
                      : ch.severity === "medium"
                        ? "bg-amber-100 dark:bg-amber-900/30"
                        : "bg-zinc-100 dark:bg-zinc-800/40"
                  }`}>
                    <span className={`text-xs font-bold ${
                      ch.severity === "high"
                        ? "text-red-600 dark:text-red-400"
                        : ch.severity === "medium"
                          ? "text-amber-600 dark:text-amber-400"
                          : "text-zinc-500 dark:text-zinc-400"
                    }`}>
                      {ch.name.slice(0, 1)}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    {/* Title line with severity + lag badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs font-semibold text-ink">{ch.name}</p>
                      {ch.severity && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                          ch.severity === "high"
                            ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
                            : ch.severity === "medium"
                              ? "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                              : "bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
                        }`}>
                          {ch.severity === "high" ? "高影响" : ch.severity === "medium" ? "中影响" : "低影响"}
                        </span>
                      )}
                      {ch.lag && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400 font-medium">
                          时滞 {ch.lag}
                        </span>
                      )}
                    </div>
                    {/* Mechanism summary (one-liner) */}
                    {ch.mechanism && (
                      <p className="text-[11px] text-ink-secondary mt-1 font-medium">
                        {ch.mechanism}
                      </p>
                    )}
                    {/* Full transmission chain */}
                    <p className="text-2xs text-ink-tertiary mt-1.5 leading-relaxed">{ch.desc}</p>
                    {/* Risk group & loan type tags */}
                    {(ch.risk_group || ch.loan_type) && (
                      <div className="flex flex-wrap gap-x-3 gap-y-1.5 mt-2">
                        {ch.risk_group && (
                          <span className="inline-flex items-center gap-1 text-[10px]">
                            <span className="text-ink-tertiary font-medium">客群</span>
                            <span className="px-1.5 py-0.5 rounded bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 border border-red-200/60 dark:border-red-800/40 font-medium">
                              {ch.risk_group}
                            </span>
                          </span>
                        )}
                        {ch.loan_type && (
                          <span className="inline-flex items-center gap-1 text-[10px]">
                            <span className="text-ink-tertiary font-medium">品种</span>
                            <span className="px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border border-amber-200/60 dark:border-amber-800/40 font-medium">
                              {ch.loan_type}
                            </span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Structured signal triggers */}
            {riskTransmission.signals && riskTransmission.signals.length > 0 && (
              <div className="border-t border-border pt-3">
                <p className="text-xs font-semibold text-ink mb-2">🎯 信号触发条件</p>
                <div className="space-y-2">
                  {riskTransmission.signals.map((sig, i) => (
                    <div key={i} className="flex gap-3 p-3 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-100/60 dark:border-blue-900/40">
                      <div className="shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center mt-0.5">
                        <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400">{i + 1}</span>
                      </div>
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="text-[11px] font-semibold text-ink">
                          触发：<span className="font-normal text-ink-secondary">{sig.trigger}</span>
                        </p>
                        <p className="text-[11px] text-ink-secondary">
                          观测：<span className="text-ink">{sig.watch}</span>
                        </p>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium">
                            {sig.lag}
                          </span>
                          {sig.note && (
                            <span className="text-[10px] text-ink-tertiary italic">{sig.note}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="px-1 py-2 border-t border-border">
              <p className="text-2xs text-ink-tertiary leading-relaxed">
                <span className="font-medium text-ink-secondary">综合研判：</span>
                {riskTransmission.signal_logic}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Full correlation table (collapsible) */}
      {correlations && correlations.length > 0 && showCredit && (
        <CorrelationTable correlations={correlations} />
      )}

      {/* Data table (last 6 periods) */}
      <div className="paper-panel overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              <th className="px-4 py-2.5 text-left text-ink-tertiary font-medium">指标</th>
              {filteredPeriods.slice(-6).map((p) => (
                <th key={p} className="px-3 py-2.5 text-right text-ink-tertiary font-medium font-mono tabular-nums">
                  {shortPeriod(p)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {indicatorList
              .filter((ind) => !hiddenCodes.has(ind.code))
              .map((ind) => (
                <tr key={ind.code} className="border-b border-border last:border-0 hover:bg-surface transition-colors">
                  <td className="px-4 py-2.5 font-medium text-ink whitespace-nowrap">
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                      style={{ backgroundColor: CHART_COLORS[indicatorList.indexOf(ind) % CHART_COLORS.length] }}
                    />
                    {ind.name}
                    <span className="text-ink-tertiary ml-1">({ind.unit})</span>
                  </td>
                  {filteredPeriods.slice(-6).map((p) => {
                    const val = ind.values[p];
                    return (
                      <td key={p} className="px-3 py-2.5 text-right font-mono tabular-nums text-ink">
                        {val ? val.value : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            {/* Credit rows */}
            {creditList.map((cs, i) => (
              <tr key={cs.key} className="border-b border-border last:border-0 hover:bg-surface transition-colors">
                <td className="px-4 py-2.5 font-medium text-ink whitespace-nowrap">
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                    style={{ backgroundColor: CREDIT_COLORS[i % CREDIT_COLORS.length] }}
                  />
                  {cs.label}
                  <span className="text-ink-tertiary ml-1">({cs.unit})</span>
                  <span className="text-2xs text-orange-500 dark:text-orange-400 ml-1.5">信贷</span>
                </td>
                {filteredPeriods.slice(-6).map((p) => {
                  const val = cs.values[p];
                  return (
                    <td key={p} className="px-3 py-2.5 text-right font-mono tabular-nums text-ink-secondary">
                      {val ? val.value : "—"}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Collapsible correlation detail table ─────────────────────────────

function CorrelationTable({ correlations }: { correlations: CreditCorrelation[] }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="paper-panel overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 flex items-center justify-between hover:bg-surface transition-colors cursor-pointer"
      >
        <span className="text-xs font-semibold text-ink">📊 相关性明细</span>
        <span className="text-2xs text-ink-tertiary">
          {correlations.length} 对指标 · {open ? "收起" : "展开"}
          <span className="inline-block ml-1 transition-transform" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
            ▾
          </span>
        </span>
      </button>
      {open && (
        <div className="overflow-x-auto border-t border-border">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-surface">
                <th className="px-4 py-2 text-left text-ink-tertiary font-medium">行业指标</th>
                <th className="px-3 py-2 text-left text-ink-tertiary font-medium">信贷指标</th>
                <th className="px-3 py-2 text-right text-ink-tertiary font-medium">Pearson r</th>
                <th className="px-3 py-2 text-right text-ink-tertiary font-medium">p-value</th>
                <th className="px-3 py-2 text-right text-ink-tertiary font-medium">Spearman ρ</th>
                <th className="px-3 py-2 text-right text-ink-tertiary font-medium">样本数</th>
                <th className="px-3 py-2 text-right text-ink-tertiary font-medium">区间</th>
              </tr>
            </thead>
            <tbody>
              {correlations.map((c, i) => {
                const abs = Math.abs(c.pearson_r);
                const rColor = abs >= 0.7
                  ? c.pearson_r > 0 ? "text-red-600 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"
                  : abs >= 0.4
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-ink-tertiary";

                return (
                  <tr key={i} className="border-b border-border last:border-0 hover:bg-surface transition-colors">
                    <td className="px-4 py-2 text-ink">{c.industry_indicator}</td>
                    <td className="px-3 py-2 text-ink-secondary">{c.credit_indicator}</td>
                    <td className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${rColor}`}>
                      {c.pearson_r > 0 ? "+" : ""}{c.pearson_r.toFixed(3)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-tertiary">
                      {c.pearson_p < 0.001 ? "<0.001" : c.pearson_p.toFixed(3)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-secondary">
                      {c.spearman_r > 0 ? "+" : ""}{c.spearman_r.toFixed(3)}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-tertiary">{c.n_periods}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-tertiary text-2xs">{c.period_range}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
